import {
  RefCallback,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react"
import isEqual from "react-fast-compare"
import { useSize } from "../size"

export type SharedSizeRect = {
  width: number
  height: number
  top: number
  left: number
  right: number
  bottom: number
}

type SharedSizeContextValue = {
  sizes: Record<string, SharedSizeRect>
  publish: (key: string, ownerId: symbol, rect: SharedSizeRect) => void
  unpublish: (key: string, ownerId: symbol) => void
}

const SharedSizeContext = createContext<SharedSizeContextValue | null>(null)

export function useSharedSizeContext() {
  const context = useContext(SharedSizeContext)
  if (!context) {
    throw new Error(
      "useSharedSizeContext must be used within a SharedSizeProvider"
    )
  }
  return context
}

/**
 * Keyed size registry for cross-component layout coordination.
 * One node per key (last publisher wins). Unpublish only clears the key when
 * the same owner still owns it — avoids the legacy ObserveSize bugs of
 * clearing whole keys incorrectly and accumulating/summing sizes.
 */
export function SharedSizeProvider({
  children
}: {
  children: React.ReactNode
}) {
  const [sizes, setSizes] = useState<Record<string, SharedSizeRect>>({})
  // Tracks which publisher currently owns each key. Unpublish ignores stale
  // owners so a remounted (or superseded) publisher can't clear a newer one.
  const ownersRef = useRef<Record<string, symbol>>({})

  const publish = useCallback(
    (key: string, ownerId: symbol, rect: SharedSizeRect) => {
      ownersRef.current[key] = ownerId
      setSizes((prev) => {
        if (isEqual(prev[key], rect)) return prev
        return { ...prev, [key]: rect }
      })
    },
    []
  )

  const unpublish = useCallback((key: string, ownerId: symbol) => {
    if (ownersRef.current[key] !== ownerId) return
    delete ownersRef.current[key]
    setSizes((prev) => {
      if (!(key in prev)) return prev
      const next = { ...prev }
      delete next[key]
      return next
    })
  }, [])

  const value = useMemo(
    () => ({ sizes, publish, unpublish }),
    [sizes, publish, unpublish]
  )

  return (
    <SharedSizeContext.Provider value={value}>
      {children}
    </SharedSizeContext.Provider>
  )
}

SharedSizeProvider.displayName = "SharedSizeProvider"

/** Observe a node and publish its rect under `key`. Returns a callback ref. */
export function useSharedSizeRef(key: string): RefCallback<HTMLElement> {
  const { publish, unpublish } = useSharedSizeContext()
  const ownerId = useMemo(() => Symbol(key), [key])
  const [setRef, rect] = useSize([
    "width",
    "height",
    "top",
    "left",
    "right",
    "bottom"
  ])

  useEffect(() => {
    publish(key, ownerId, rect)
    return () => unpublish(key, ownerId)
  }, [key, ownerId, rect, publish, unpublish])

  return setRef
}

/** Read the current rect for a key (undefined if unpublished). */
export function useSharedSize(key: string): SharedSizeRect | undefined {
  return useSharedSizeContext().sizes[key]
}

/** Read multiple keys at once. */
export function useSharedSizes(
  keys: string[]
): Record<string, SharedSizeRect | undefined> {
  const { sizes } = useSharedSizeContext()
  return useMemo(() => {
    const result: Record<string, SharedSizeRect | undefined> = {}
    for (const key of keys) {
      result[key] = sizes[key]
    }
    return result
  }, [sizes, keys.join("\0")])
}
